import type { Messages } from '../messages.js'

/**
 * Estonian. `käik` for flips, the word an Estonian board game uses for a move.
 *
 * Estonian has two plural forms and they are not singular and plural: a number takes the
 * partitive, so it is 1 käik but 2 käiku and 20 käiku. For `sõna` the two happen to coincide,
 * which is why that pair looks like a mistake and is not.
 */
export const et: Messages = {
  tag: 'et',

  readingDictionary: 'Loen sõnaraamatut…',
  noWordList: '„{language}“ jaoks pole sõnaloendit. Koostage see:  pnpm dictionary build',
  emptyWordList: '„{language}“ sõnaloend on tühi.',

  flips: 'käigud',
  score: 'punktid',
  words: 'sõnad',
  round: 'voor',
  ticksLeftLabel: 'Selles voorus jäänud aeg',
  typeAWord: 'kirjutage sõna',
  tapPrompt: 'puudutage tähti valimiseks või tagasivõtmiseks, siis {action}',

  boardOfTiles: '{n} nupuga laud',
  faceDown: 'kummuli',
  wildCard: 'joker',
  wildKey: 'ükskõik milline täht',
  letterReplaced: '{from} sai {to}-ks',
  letterSwap: 'TÄHEVAHETUS!',
  spentTile: 'kasutatud nupp',
  hiddenWhilePaused: 'pausi ajal peidetud',
  letterInWord: '{letter}, sõna {position}. täht',

  completeWord: 'Lõpeta sõna',

  completeShort: 'Valmis',
  reset: 'Tühjenda',
  pause: 'Paus',
  resume: 'Jätka',
  newGame: 'Uus mäng',
  paused: 'Peatatud',
  outOfFlips: 'Käigud otsas',
  finalResult: '{score} {words} eest {rounds} jooksul',
  playAgain: 'Mängi uuesti',
  share: 'Jaga',
  shareCopied: 'Kopeeritud.',
  shareSelect: 'Kopeerige see:',

  lettersSelect: 'tähed valivad',
  keysWild: 'võetakse, kui kirjutate tähe, mida ükski nupp ei näita',
  clearsEvery: 'tühjendab kõik valitud {letter}',
  undoLastLetter: 'võta viimane täht tagasi',
  noWordsYet: 'Veel ühtegi sõna.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'juba leitud',
  reasonTooShort: 'liiga lühike',
  reasonNotAWord: 'see pole sõna',
  reasonAllFound: 'need on teil juba kõik',
  noSuchLetterUp: 'ühtegi {letter} pole lahti',
  nothingUp: 'midagi pole lahti',
  letterHidden: 'üks täht peitis end',
  shuffled: 'segatud',
  shuffledAndBilled: 'segatud, arvestati {flips} kasutamata',
  tooFewLetters: 'Liiga vähe tähti jäänud',

  gameLanguage: 'keel',
  interfaceLanguage: 'liides',
  dictionarySize: '{common} tavalist {full} sõnast',
  filterLanguages: 'Otsi keelt',
  noMatches: 'Vasteid pole',

  nerdMode: 'nohikurežiim',
  rules: 'Reeglid',
  difficulty: 'raskusaste',
  difficultyNames: { easy: 'lihtne', medium: 'keskmine', hard: 'raske', insane: 'hullumeelne' },
  pausedNoBoard: 'See mäng edetabelisse ei jõua.',
  about: 'Teave',
  themeLabel: 'teema',
  themeNames: { traditional: 'traditsiooniline', light: 'hele', contrast: 'suur kontrast' },
  tiles: 'nupud (N)',
  secondsPerTick: 'sekundid / takt',
  holdTicks: 'hoiutaktid',
  minWord: 'lühim sõna',
  startingFlips: 'algkäigud',
  wildChance: 'jokeri tõenäosus',
  replaceChance: 'tähevahetuse tõenäosus',
  hideChance: 'tähe peitmise tõenäosus',
  wordCompleteMode: 'kui sõna on valmis',
  wordCompleteNames: { shuffle: 'sega', spend: 'kuluta', keep: 'jäta alles' },
  flipEconomy: 'käikude tagastus',
  flipEconomyNames: {
    none: 'puudub',
    perLetter: 'tähe kohta',
    fibonacci: 'fibonacci',
    overMinimum: 'üle miinimumi',
  },
  repeatedLetterKey: 'korduva tähe klahv',
  keySchemeNames: { cycle: 'ring', advance: 'edasi' },
  keySchemeHelp: {
    cycle:
      'A võtab järgmise kasutamata A, ja kui need kõik on sõnas, tühjendab need. ' +
      'Shift+A tühjendab need samuti.',
    advance: 'A võtab järgmise kasutamata A. Shift+A tühjendab sõnast kõik A-d.',
  },

  whatThatMeans: 'Mida see tähendab',
  factRound: 'voor',
  factWholeBoardUp: 'kogu laud lahti',
  factRoundCosts: 'voor maksab',
  factFlipsBuy: 'algkäigud ostavad',
  factThisBoard: 'see laud',
  factBoardHadToAdmit: 'laud pidi lubama',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, pikim {longest}',
  wordsIncludingOneOf: '{words}, neist üks {ceiling}',
  scorelessRounds: '{rounds} punktideta',

  whatAWordPays: 'Mida sõna toob',
  columnLetters: 'tähed',
  columnCost: 'hind',
  columnPoints: 'punktid',
  columnFlips: 'käigud',
  columnNet: 'vahe',

  canonicalRules: '{difficulty} tavareeglid.',
  customRules: 'Muudetud eelseadest. Omareeglitega tulemused edetabelisse ei lähe.',
  applyAndStart: 'Rakenda ja alusta uut mängu',
  changesNextGame: 'Muudatused jõustuvad järgmises mängus.',
  presets: 'Eelseaded:',

  start: 'Alusta',
  restart: 'Uuesti',
  quit: 'Lõpeta',
  quitTitle: 'Kas lõpetada see mäng?',
  restartTitle: 'Kas alustada see mäng uuesti?',
  restartConfirm: 'Uuesti',
  quitConfirm: 'Lõpeta',
  keepPlaying: 'Mängi edasi',
  personalBest: 'Teie parimad mängud',
  newPersonalBest: 'Uus isiklik rekord.',
  columnRank: '#',
  notRanked: 'Omareeglid, seega see mäng edetabelisse ei lähe.',
  notRankedPaused: 'Kell peatus, seega see mäng edetabelisse ei lähe.',
  rankOfTotal: '{rank} / {total}',

  howToPlay: 'Kuidas mängida',

  backToGame: 'Tagasi mängu',
  welcomeTitle: 'Tere tulemast Blinkeredisse',
  tutorialSkip: 'Jäta vahele',
  tutorialNext: 'Edasi',
  tutorialBack: 'Tagasi',
  tutorialStart: 'Hakka mängima',
  tutorialHideAgain: 'Ära seda enam näita',
  tutorialProgress: '{n} / {total}',
  tutorialSkipTitle: 'Kas jätta tutvustus vahele?',
  tutGoalTitle: 'Blinkered on võidujooks',
  tutGoalBody:
    'Alustate mängu kindla arvu käikudega ja iga pöörduv nupp maksab ühe käigu. Kui käigud otsa saavad, on mäng läbi. Sõnad toovad käike juurde, ja mida pikem sõna, seda rohkem käike saate.',
  tutSpentBody:
    'Sõnas kasutatud tähed jäävad kummuli kuni järgmise vooruni. Ainult tähevahetus muudab, millised tähed laual on.',
  tutChangeTitle: 'Tähed võivad muutuda',
  tutSameLetters:
    'Märkasite sõna, mida ei jõudnud mängida? Samad tähed tulevad järgmises voorus tagasi, uutesse kohtadesse segatuna.',
  tutPickLetters: 'Puudutage soovitud tähti järjekorras, et moodustada sõna.',
  tutMoreTurn: 'Nupud pöörduvad ka mõtlemise ajal, nii et parem täht võib alles tulla.',
  tutTapBack:
    'Võtsite tähe, mida ei tahtnud? Puudutage seda uuesti ja see läheb tagasi. Ükskõik millise, mitte ainult viimase.',
  tutComplete: 'Kui sõna on valmis, vajutage Valmis.',
  htBoardTitle: 'Laud',
  htBoardBody:
    'Nupud pöörduvad ükshaaval, juhuslikus järjekorras. Lahtistest nuppudest moodustatakse sõnu.',
  htWordsTitle: 'Sõnad',
  htWordsBody: 'Moodustage lahtistest nuppudest sõna, kirjutades või klõpsates tähti järjekorras.',
  htFlipsTitle: 'Käigud',
  htFlipsBody:
    'Iga pöörduv nupp maksab käigu. Valmis sõna toob käike tagasi, ja pikemad sõnad toovad rohkem. Kui käigud otsa saavad, on mäng läbi.',
  htRoundTitle: 'Voor',
  htRoundBody:
    'Kui vooru viimane nupp pöördub, seisab kogu laud hetke paigal. Siis pööratakse nupud kummuli ja segatakse, ja algab uus voor.',
  htLanguagesTitle: 'Keeled',
  htLanguagesBody:
    'Neid on {n}. Iga laua saab lahendada sõnadega, mida inimesed tõesti kasutavad. Haruldane sõna annab samuti punkte, kui sõnaraamat seda teab.',
  htKeysTitle: 'Klaviatuur',
  htWildTitle: 'Jokerid',
  htWildBody:
    'Vahel ilmub tähe asemel joker. Joker läheb arvesse iga tähena, mis moodustab kehtiva sõna. Juba leitud sõna ei lähe arvesse.',
  htHideTitle: 'Peituvad tähed',
  htHideBody:
    'Raskematel tasemetel võib avatud täht ise tagasi pöörduda ja tulla vooru jooksul hiljem uuesti. Taimer saab selle eest lisatakti, punasega. Juba valitud tähed tagasi ei pöördu.',
  tutHideBody:
    'Raskematel tasemetel võib avatud täht ise tagasi pöörduda ja tulla vooru jooksul hiljem uuesti. Juba valitud tähed tagasi ei pöördu.',
  htSwapTitle: 'Muutuvad tähed',
  htSwapBody:
    'Vahel vahetatakse voorude vahel üks täht teise vastu. Näete, milline kadus ja milline lisandus.',
  htLevelsTitle: 'Raskusastmed',
  htLevelEasy:
    'Samad kaksteist tähte terve mängu, nii et need saab selgeks õppida ja sõnade nimekirja peas kanda. Nupud pöörduvad aeglaselt ja kogu laud jääb piisavalt kauaks nähtavale, et valik lõpetada.',
  htLevelMedium:
    'Aeg-ajalt üks täht muutub, nii et hiljemaks hoitud sõnu on raskem meeles pidada. Vähem aega vaadata ja vähem mõelda.',
  htLevelHard:
    'Kolmetähelised sõnad ei loe enam, ja täht muutub umbes üle vooru. Laud jõuab vaevu näida, kui juba segatakse.',
  htLevelInsane: 'Kõik korraga, täiskiirusel. Laud segatakse peaaegu kohe pärast viimast käiku.',
  htTouchTitle: 'Puuteekraan',
  htTouchBody:
    'Puudutage lahtist nuppu, et selle täht võtta. Puudutage võetud tähte, et see tagasi anda. Valmis ja Tühjenda on laua all.',

  accountTitle: 'Sinu konto',
  menuProfile: 'Minu profiil',
  menuGames: 'Minu mängud',
  menuPublicPage: 'Minu avalik leht',
  menuSignedInAs: 'Sisse logitud kui',
  signIn: 'Logi sisse',
  signOut: 'Logi välja',
  signInTitle: 'Logi sisse või loo konto',
  signInLead: 'Konto hoiab sinu mängud alles, igas seadmes.',
  signInKeepGame: 'Logi sisse, et see mäng alles jääks, ja kõik järgnevad.',
  continueWithApple: "Jätka Apple'iga",
  continueWithGoogle: "Jätka Google'iga",
  signInOr: 'või',
  emailLabel: 'E-post',
  codeLabel: 'Kood',
  emailMeACode: 'Saatke mulle kood',
  working: 'Hetk…',
  sendNewCode: 'Saada uus kood',
  codeSent: 'Vaata e-posti. Kood kehtib 10 minutit.',
  notNow: 'Mitte praegu',
  badEmail: 'See ei näe välja nagu e-posti aadress.',
  badCode: 'See kood ei sobinud. Koodid aeguvad 10 minutiga ja kehtivad korra.',
  serverBusy: 'Serverini ei jõudnud. Proovi hetke pärast uuesti.',
  serverDown: 'Serverini ei jõudnud.',
  offline: 'Ühenduseta',
  leaderboardTitle: 'Edetabel',
  leaderboardEmpty: 'Selles edetabelis pole veel mänge. Ole esimene.',
  leaderboardWouldBe: 'Kus see mäng asuks',
  leaderboardThisGame: 'See mäng',
  leaderboardRanksHere: 'Kus see mäng asub',
  leaderboardOutside: 'Väljaspool esiviisikut.',
  tutAccountTitle: 'Hoia oma tulemused alles',
  tutAccountBody:
    'Konto salvestab sinu mängud, paneb tulemused edetabelisse ja hoiab sinu profiili.',
  ssoCancelled: 'Sisselogimine katkestati.',
  ssoExpired: 'See võttis liiga kaua. Logi uuesti sisse.',
  ssoNoUsername: 'Konto loomine jäi pooleli. Proovi hetke pärast uuesti.',
  ssoFailed: 'Ei õnnestunud. Proovi uuesti või kasuta e-posti.',
  tabProfile: 'Profiil',
  countryAny: 'Ei ütle',
  findCountry: 'Otsi riike',
  usernameLabel: 'Kasutajanimi',
  bioLabel: 'Minust',
  countryLabel: 'Riik',
  bioHint: 'Linke ei tohi. {left}/{max}',
  save: 'Salvesta',
  saving: 'Salvestan…',
  saved: 'Salvestatud.',
  nameTaken: 'See nimi on juba kellelgi.',
  nameTooShort: 'Natuke pikem, palun.',
  nameTooLong: 'See on liiga pikk.',
  nameBadCharacters: 'Ainult tähed, numbrid, sidekriipsud ja alakriipsud.',
  nameBadEdges: 'Peab algama ja lõppema tähe või numbriga.',
  nameMixedScripts: 'Vali üks tähestik ja jää selle juurde.',
  nameReserved: 'See näeb välja nagu nimi, mida me ise jagame. Vali teine.',
  nameUnusable: 'Seda nime ei saa kasutada.',
  bioTooLong: 'Siia mahub kuni {max} märki.',
  bioHasLink: 'Linke siia ei tohi.',
  bioHasControl: 'Siin on märke, mis siia ei mahu.',
  bioUnusable: 'Seda teksti ei saa kasutada.',
  gamesHeading: 'Mängud',
  gamesEmpty: 'Siin pole veel midagi. Sisse logituna mängitud mängud ilmuvad siia.',
  gamesEmptyShort: 'Siin pole veel midagi.',
  gamesLoading: 'Loen sinu mänge…',
  theirGamesLoading: 'Loen nende mänge…',
  profileLoading: 'Loen profiili…',
  playerNotFound: 'Siin pole kedagi selle nimega.',
  columnWhen: 'Millal',
  columnRounds: 'vooru',
  columnGame: 'Mäng',
  gameLoading: 'Loen mängu…',
  gameUnreadable: 'Seda mängu ei õnnestunud lugeda.',
  gameNotRanked: 'Mängitud muudetud reeglitega, seega ei lähe arvesse.',
  detailNotKept: 'Selle mängu sõnu ja laudu enam ei hoita.',
  openingBoard: 'Algne laud',
  roundByRound: 'Voor voorult',
  roundNothing: 'midagi ei leitud',
  changeReplaced: '{letter} asendatud kohal {slot}',
  changeWildOn: '{letter} jokker kohal {slot}',
  changeWildOff: '{letter} jokker läks kohalt {slot}',
  keepThisGame: 'Hoia see mäng alles',
  savedToYourGames: 'Salvestatud sinu mängude hulka.',

  reportThis: 'Teata',
  reportHeading: 'Teata sellest',
  reportTheName: 'Nimi',
  reportTheBio: 'Tutvustus',
  reportTheScore: 'Tulemus',
  reportWhy: 'Mis sellega valesti on? Valikuline.',
  reportSend: 'Saada teade',
  reportSent: 'Aitäh. Keegi vaatab selle üle.',
  reportSignIn: 'Logi sisse, et millestki teatada.',
  reportGone: 'Seda enam ei ole.',
  reportCancel: 'Tühista',

  deleteAccount: 'Kustuta mu konto',
  deleteAccountWhat:
    'See eemaldab jäädavalt sinu profiili ja kõik salvestatud mängud. Seda ei saa tagasi võtta.',
  deleteAccountAsk: 'Saada mulle kood',
  deleteConfirm: 'Kustuta jäädavalt',
  deleteGone: 'Sinu kontot enam ei ole. Aitäh, et mängisid.',
  deleteNoAddress:
    'Sellel kontol pole kinnitatud aadressi, seega ei saa kustutamist kontrollida. Võta ühendust ja teeme seda käsitsi.',

  rulesetFacts: 'Sõnad alates {letters} · uus nupp iga {seconds} järel',
  plurals: {
    words: { one: '{n} sõna', other: '{n} sõna' },
    rounds: { one: '{n} voor', other: '{n} vooru' },
    flips: { one: '{n} käik', other: '{n} käiku' },
    ticks: { one: '{n} takt', other: '{n} takti' },
    points: { one: '{n} punkt', other: '{n} punkti' },
    minLetters: { one: '{n} tähest', other: '{n} tähest' },
    seconds: { one: '{n} sekundi', other: '{n} sekundi' },
  },
}
